/**
 * Desktop tablist ↔ tabpanel wiring for the smart tabs section.
 * Mobile uses native <details>; this only runs the desktop tab bar.
 */
class SmartTabsSection extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const tablist = this.querySelector('[data-smart-tabs-tablist]');
    if (!(tablist instanceof HTMLElement)) return;

    const tabs = [...tablist.querySelectorAll('[role="tab"]')];
    const panels = [...this.querySelectorAll('[data-smart-tabs-panel]')];

    if (tabs.length === 0 || panels.length === 0 || tabs.length !== panels.length) return;

    const activate = (index) => {
      tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel, i) => {
        if (panel instanceof HTMLElement) {
          panel.toggleAttribute('hidden', i !== index);
        }
      });
    };

    const initial = Math.max(
      0,
      tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')
    );
    activate(initial === -1 ? 0 : initial);

    tablist.addEventListener(
      'click',
      (e) => {
        const tab = e.target instanceof Element ? e.target.closest('[role="tab"]') : null;
        if (!tab || !(tab instanceof HTMLElement) || !tablist.contains(tab)) return;
        const idx = tabs.indexOf(tab);
        if (idx >= 0) activate(idx);
      },
      { signal }
    );

    tablist.addEventListener(
      'keydown',
      (e) => {
        if (!(e instanceof KeyboardEvent)) return;
        const current = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
        if (current < 0) return;

        let next = current;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          next = (current + 1) % tabs.length;
          e.preventDefault();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          next = (current - 1 + tabs.length) % tabs.length;
          e.preventDefault();
        } else if (e.key === 'Home') {
          next = 0;
          e.preventDefault();
        } else if (e.key === 'End') {
          next = tabs.length - 1;
          e.preventDefault();
        }

        if (next !== current) {
          activate(next);
          tabs[next]?.focus();
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }
}

if (!customElements.get('smart-tabs-section')) {
  customElements.define('smart-tabs-section', SmartTabsSection);
}
